/**
 * Bear history and result entry (spec sections 24, 35, 72).
 *
 * Recording is live here rather than hidden behind the planner, because it is
 * what actually happens: the session ends, the result screen is up, and one R4
 * types it in before it disappears.
 *
 * The listing below it is the same data read back. Its job is to make the gap
 * between "what happened" and "what has been typed in" impossible to miss: a
 * DRAFT session's totals are partial sums, and a session with 12 of 37
 * participants entered must not read like a finished result.
 */

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAllianceMembers, useBearSessions, useCreateBearSession } from '../../api/queries';
import { useCoreContext } from '../../lib/coreAlliance';
import { Badge, Card, Notice, PageHeader, Value } from '../../components/ui/primitives';
import { EmptyState, ErrorState, LoadingBlock, QueryBoundary } from '../../components/ui/states';
import { formatCompact, formatDateTime, formatNumber } from '../../lib/format';
import { useT } from '../../i18n';
import { RallyEntryForm } from './RallyEntryForm';
import { SessionDetail } from './SessionDetail';
import { EntryProgress, EntryStatusBadge } from './SessionEntryProgress';

export function BearHistoryPage() {
  const t = useT();
  const core = useCoreContext();
  const members = useAllianceMembers(core.allianceId ?? undefined);
  const createSession = useCreateBearSession();

  /**
   * The open session lives in the URL, not in component state.
   *
   * Entering a Bear session is long, interrupted work -- an operator reads a
   * result screen, switches away, comes back, reloads. Holding the id in
   * useState meant a refresh silently dropped it and the page looked like the
   * session had been lost, even though it was safely in the database and
   * listed further down. A search param survives reload, back/forward, and
   * being pasted to someone else.
   */
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionId = searchParams.get('session');
  const setSessionId = (id: string | null) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (id) next.set('session', id);
        else next.delete('session');
        return next;
      },
      { replace: true },
    );
  };
  const [manualId, setManualId] = useState('');
  const [trapNumber, setTrapNumber] = useState('');
  const [recorded, setRecorded] = useState(0);
  const [inspectedId, setInspectedId] = useState<string | null>(null);

  if (core.isPending) return <LoadingBlock label={t.common.loadingAlliance} />;
  if (core.error) return <ErrorState error={core.error} />;

  if (!core.allianceId) {
    return (
      <>
        <PageHeader title={t.bearHistory.title} />
        <EmptyState
          title={t.common.noCoreAllianceTitle}
          body={t.bearHistory.noCoreAllianceBody}
          action={
            <Link className="btn btn--sm" to="/data/sync">
              {t.common.openSyncStatus}
            </Link>
          }
        />
      </>
    );
  }

  const allianceId = core.allianceId;

  return (
    <>
      <PageHeader title={t.bearHistory.title} subtitle={t.bearHistory.subtitle} />

      <div className="stack">
        <Card title={t.bearHistory.session}>
          {sessionId ? (
            <div className="row">
              <Badge tone="accent">{t.bearHistory.recordingInto}</Badge>
              <span className="mono">{sessionId}</span>
              {recorded > 0 ? (
                <span className="muted small">{t.bearHistory.savedThisSitting(recorded)}</span>
              ) : null}
              <button
                type="button"
                className="btn btn--sm btn--subtle"
                onClick={() => {
                  setSessionId(null);
                  setRecorded(0);
                }}
              >
                {t.bearHistory.closeSession}
              </button>
            </div>
          ) : (
            <div className="row" style={{ gap: 16, alignItems: 'flex-end' }}>
              <label className="row" style={{ gap: 6 }}>
                <span className="muted small">{t.bearHistory.trapNumber}</span>
                <input
                  type="number"
                  min={1}
                  value={trapNumber}
                  onChange={(event) => setTrapNumber(event.target.value)}
                  placeholder={t.common.optional}
                  style={{ width: 96 }}
                />
              </label>
              <button
                type="button"
                className="btn btn--primary"
                disabled={createSession.isPending}
                onClick={() =>
                  createSession.mutate(
                    {
                      allianceId,
                      ...(trapNumber.trim() ? { trapNumber: Number(trapNumber) } : {}),
                      startedAt: new Date().toISOString(),
                    },
                    { onSuccess: (created) => setSessionId(created.id) },
                  )
                }
              >
                {createSession.isPending ? t.bearHistory.starting : t.bearHistory.startSession}
              </button>

              <span className="muted small">{t.bearHistory.orContinue}</span>

              <input
                value={manualId}
                onChange={(event) => setManualId(event.target.value)}
                placeholder={t.bearHistory.sessionIdPlaceholder}
                className="mono"
                style={{ width: 300 }}
                aria-label={t.bearHistory.existingSessionIdAria}
              />
              <button
                type="button"
                className="btn"
                disabled={!manualId.trim()}
                onClick={() => setSessionId(manualId.trim())}
              >
                {t.bearHistory.continue}
              </button>
            </div>
          )}

          {createSession.error ? (
            <div style={{ marginTop: 10 }}>
              <ErrorState error={createSession.error} />
            </div>
          ) : null}
        </Card>

        {sessionId ? (
          <QueryBoundary
            query={members}
            loadingLabel={t.common.loadingRoster}
            isEmpty={(data) => data.members.length === 0}
            empty={
              <EmptyState title={t.bearHistory.noRosterTitle} body={t.bearHistory.noRosterBody} />
            }
          >
            {(data) => (
              <RallyEntryForm
                sessionId={sessionId}
                members={data.members}
                onRecorded={() => setRecorded((count) => count + 1)}
              />
            )}
          </QueryBoundary>
        ) : (
          <Notice severity="INFO">{t.bearHistory.startToRecord}</Notice>
        )}

        <PastSessions
          openSessionId={inspectedId}
          enteringSessionId={sessionId}
          onInspect={setInspectedId}
          onContinue={(id: string) => {
            setSessionId(id);
            setRecorded(0);
          }}
        />
      </div>
    </>
  );
}

/**
 * The session log.
 *
 * Sorted newest first, and every row carries its entry state. A session an R4
 * abandoned halfway through must be identifiable at a glance — otherwise the
 * next person to open the app reads a quarter-entered night as a bad night.
 */
function PastSessions({
  openSessionId,
  enteringSessionId,
  onInspect,
  onContinue,
}: {
  openSessionId: string | null;
  /** The session the form above is currently recording into, if any. */
  enteringSessionId: string | null;
  onInspect: (id: string | null) => void;
  onContinue: (id: string) => void;
}) {
  const t = useT();
  const sessions = useBearSessions();
  const columns = t.bearHistory.columns;

  return (
    <div className="stack">
      <Card title={t.bearHistory.pastSessions}>
        <QueryBoundary
          query={sessions}
          loadingLabel={t.bearHistory.loadingSessions}
          isEmpty={(data) => data.sessions.length === 0}
          empty={
            <EmptyState
              title={t.bearHistory.noSessionsTitle}
              body={t.bearHistory.noSessionsBody}
            />
          }
        >
          {(data) => {
            const drafts = data.sessions.filter((s) => s.entryStatus === 'DRAFT').length;
            return (
              <>
                {drafts > 0 ? (
                  <div style={{ marginBottom: 10 }}>
                    <Notice severity="WARN">{t.bearHistory.draftsNotice(drafts)}</Notice>
                  </div>
                ) : null}

                <div className="table-wrap">
                  <table className="data">
                    <thead>
                      <tr>
                        <th>{columns.session}</th>
                        <th>{columns.started}</th>
                        <th>{columns.entry}</th>
                        <th>{columns.coverage}</th>
                        <th className="num">{columns.rallies}</th>
                        <th className="num">{columns.allianceDamage}</th>
                        <th />
                      </tr>
                    </thead>
                    <tbody>
                      {[...data.sessions]
                        .sort(
                          (a, b) =>
                            new Date(b.startedAt ?? b.createdAt).getTime() -
                            new Date(a.startedAt ?? a.createdAt).getTime(),
                        )
                        .map((session) => (
                          <tr key={session.id}>
                            <td>
                              {session.trapNumber === null ? (
                                <span className="absent">{t.bearHistory.noTrapNumber}</span>
                              ) : (
                                <strong>{t.bearHistory.trapLabel(session.trapNumber)}</strong>
                              )}
                            </td>
                            <td className="nowrap">
                              <Value absent={t.common.notRecorded}>
                                {formatDateTime(session.startedAt)}
                              </Value>
                            </td>
                            <td>
                              <EntryStatusBadge status={session.entryStatus} />
                            </td>
                            <td>
                              <EntryProgress session={session} />
                            </td>
                            <td className="num">{formatNumber(session.rallyCount)}</td>
                            <td className="num">
                              {session.allianceTotalDamage === null ? (
                                <span className="absent">{t.common.notRecorded}</span>
                              ) : (
                                <span
                                  title={
                                    session.entryStatus === 'DRAFT'
                                      ? t.bearHistory.partialTitle
                                      : undefined
                                  }
                                >
                                  {formatCompact(session.allianceTotalDamage)}
                                  {session.entryStatus === 'DRAFT' ? (
                                    <span className="faint"> {t.bearHistory.partialSuffix}</span>
                                  ) : null}
                                </span>
                              )}
                            </td>
                            <td>
                              <span className="row" style={{ gap: 4 }}>
                                <button
                                  type="button"
                                  className="btn btn--sm"
                                  onClick={() =>
                                    onInspect(openSessionId === session.id ? null : session.id)
                                  }
                                >
                                  {openSessionId === session.id ? t.common.hide : t.common.open}
                                </button>
                                {session.id === enteringSessionId ? (
                                  <Badge tone="info">{t.bearHistory.enteringNow}</Badge>
                                ) : session.entryStatus === 'DRAFT' ? (
                                  <button
                                    type="button"
                                    className="btn btn--sm"
                                    onClick={() => onContinue(session.id)}
                                    title={t.bearHistory.continueEntryTitle}
                                  >
                                    {t.bearHistory.continueEntry}
                                  </button>
                                ) : null}
                              </span>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>

                {data.total > data.sessions.length ? (
                  <p className="faint small" style={{ marginBottom: 0 }}>
                    {t.bearHistory.showingRecent(
                      data.sessions.length,
                      formatNumber(data.total) ?? '',
                    )}
                  </p>
                ) : null}
              </>
            );
          }}
        </QueryBoundary>
      </Card>

      {openSessionId ? (
        <SessionDetail sessionId={openSessionId} onClose={() => onInspect(null)} />
      ) : null}
    </div>
  );
}
